// External payment gateway ("CyberExt") — NOT part of the SFMIS-Backend
// Laravel API. It's a separate third-party payment portal, reached by a
// plain HTML form POST rather than an AJAX call:
//   - A script-initiated fetch/axios call would very likely be blocked by
//     CORS, since this host has no reason to allow our frontend's origin.
//   - The gateway's response is a page meant for the payer to interact with
//     (its own redirect / card / OTP screen), not JSON to read programmatically.
// A real <form> submission is a normal browser navigation, exempt from CORS,
// and can target a popup window directly — so that's what this does, mirroring
// the reference integration:
//   curl --location 'http://10.248.32.15/cyberext/UserVacancy.php' \
//     --form 'fLname="..."' --form 'NWI="..."' --form 'nic="..."' \
//     --form 'mobile="..."' --form 'address="..."' --form 'IncomeCode="..."' \
//     --form 'dill="..."' --form 'amu="..."' --form 'email="..."' \
//     --form 'reference2="..."'
//
// The field values themselves are never built here: they come verbatim as the
// `payment_payload` object returned by the backend's registration endpoints
// (POST /api/member-registrations, /api/club-registrations) or by /verify-otp
// (registration_process.payment_payload) for a resumed unpaid registration.
// Callers pass that object straight through to openPaymentGateway.

const GATEWAY_URL = "https://payments.sjp.ac.lk/cyberext/UserVacancy.php";

// Fixed size/chrome for the payment popup. Omitting toolbar/menubar/location
// is what makes browsers render this as a popup window rather than a new tab.
const POPUP_FEATURES = "width=520,height=720,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes";
const POPUP_NAME = "cyberGatewayPayment";

// Submits `fields` to the external gateway as a real multipart form POST
// (matching the reference `curl --form ...` call) and opens the response in a
// small popup window (named `cyberGatewayPayment`) instead of a new tab.
// Reusing the same window name means a second call re-navigates the existing
// popup rather than spawning another one.
//
// Must be called synchronously from inside the triggering click handler —
// both window.open() and a form submit targeting it can get blocked as a
// popup by some browsers if invoked after an `await`.
//
// Returns the popup's `Window` handle so the caller can poll `.closed` —
// that's readable cross-origin (unlike `.location`/content), and is how
// PaymentMethod knows when to check the real payment status with the backend.
export function openPaymentGateway(fields) {
  const popup = window.open("", POPUP_NAME, POPUP_FEATURES);

  const form = document.createElement("form");
  form.method = "POST";
  form.action = GATEWAY_URL;
  form.enctype = "multipart/form-data";
  form.target = POPUP_NAME;
  form.style.display = "none";

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value ?? "";
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  form.remove();

  popup?.focus();
  return popup;
}
